import {GirafeHTMLElement} from '@geogirafe/lib-geoportal/base';
import {IGirafePanel} from '@geogirafe/lib-geoportal/core';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import CircleStyle from 'ol/style/Circle';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import type {FeatureLike} from 'ol/Feature';
import type OlMap from 'ol/Map';

type MistraMode = 'linear' | 'areal';

interface AxisResult {
  layer: string;
  axisType: string;
  axisName: string;
  sectorName: string;
  sectorLength: number;
  totalLength: number;
  axisId: string;
}

interface MistraApiResponse {
  distance_error?: boolean;
  wrong_axis_correspondance?: boolean;
  multigeometry?: boolean;
}

const LINEAR_LAYERS = ['rt10_axes_nationaux', 'rt11_axes_cantonaux', 'rt12_axes_communaux'];
const AREAL_LAYERS = ['rt11_axes_cantonaux'];
const AXIS_TYPE_LABELS: Record<string, string> = {
  rt10_axes_nationaux: 'Axes routes nationales',
  rt11_axes_cantonaux: 'Axes routes cantonales',
  rt12_axes_communaux: 'Axes routes communales',
};

class SitnMistradistanceComponent extends GirafeHTMLElement implements IGirafePanel {
  override templateUrl = './template.html';
  styleUrls = ['./style.css', '../../../node_modules/@geogirafe/lib-geoportal/styles/common.css'];

  isPanelVisible = false;
  panelTitle = 'mistradistance-panel';
  panelTogglePath = 'extendedState.interface.mistradistancePanelVisible';

  mode: MistraMode = 'linear';
  loading = false;
  distanceError = false;
  wrongAxisError = false;
  multigeometryError = false;

  result: AxisResult | null = null;
  result2: AxisResult | null = null;
  intersectionLength = 0;
  intersectionArea = 0;

  private requestedPoint = 0;
  private firstPoint: number[] = [];

  private readonly vectorSource = new VectorSource();
  private readonly vectorLayer: VectorLayer<VectorSource>;
  private olMap: OlMap | null = null;
  private clickHandler: ((e: {coordinate: number[]}) => void) | null = null;

  constructor() {
    super('sitn-mistradistance');
    this.vectorLayer = new VectorLayer({
      source: this.vectorSource,
      style: (feature) => this.styleFunction(feature),
      zIndex: 100,
    });
  }

  private styleFunction(feature: FeatureLike): Style | undefined {
    const geomType = feature.getGeometry()?.getType();
    if (geomType === 'Point') {
      return new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({color: 'rgba(0, 255, 0, 0.4)'}),
          stroke: new Stroke({color: 'rgba(0, 255, 0, 0.6)', width: 1}),
        }),
      });
    }
    if (geomType === 'LineString') {
      return new Style({stroke: new Stroke({color: 'rgba(0, 255, 0, 0.6)', width: 5})});
    }
    if (geomType === 'Polygon') {
      return new Style({
        stroke: new Stroke({color: 'rgba(200, 0, 0, 0.6)', width: 2}),
        fill: new Fill({color: 'rgba(200, 0, 0, 0.3)'}),
      });
    }
    return undefined;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  override render() {
    super.render();
    if (this.isPanelVisible) {
      this.show();
    } else {
      this.hide();
    }
  }

  togglePanel(visible: boolean) {
    if (this.isPanelVisible === visible) return;
    this.isPanelVisible = visible;
    if (visible) {
      this.openPanel();
    } else {
      this.closePanel();
    }
    this.render();
  }

  setMode(mode: MistraMode) {
    if (this.mode === mode) return;
    this.mode = mode;
    this.resetResults();
    this.refreshRender();
  }

  private openPanel() {
    const map = this.context.mapManager.getMap();
    this.olMap = map;
    this.vectorLayer.setMap(map);
    this.registerInteractionListener('map.select', true);
    const handler = (e: {coordinate: number[]}) => {
      if (this.canExecute('map.select')) {
        void this.onMapClick(e.coordinate[0], e.coordinate[1]);
      }
    };
    this.clickHandler = handler;
    map.on('singleclick', handler as (e: object) => void);
    const target = map.getTargetElement();
    if (target instanceof HTMLElement) {
      target.style.cursor = 'crosshair';
    }
  }

  private closePanel() {
    const map = this.olMap;
    if (map) {
      const handler = this.clickHandler;
      if (handler) {
        map.un('singleclick', handler as (e: object) => void);
        this.clickHandler = null;
      }
      const target = map.getTargetElement();
      if (target instanceof HTMLElement) {
        target.style.cursor = '';
      }
      this.olMap = null;
    }
    this.vectorLayer.setMap(null);
    this.resetResults();
    this.unregisterInteractionListeners();
    this.loading = false;
  }

  private resetResults() {
    this.vectorSource.clear();
    this.requestedPoint = 0;
    this.firstPoint = [];
    this.result = null;
    this.result2 = null;
    this.intersectionLength = 0;
    this.intersectionArea = 0;
    this.distanceError = false;
    this.wrongAxisError = false;
    this.multigeometryError = false;
  }

  private async onMapClick(x: number, y: number) {
    // Mirrors the legacy ngeo plugin: a new areal measurement cycle starts fresh,
    // but the point in the middle of the cycle (requestedPoint === 1) must keep
    // the first click's axis/first_point to be sent along with the second click.
    if (this.mode !== 'areal' || this.requestedPoint === 0 || this.requestedPoint === 2) {
      this.resetResults();
    }

    let layers = LINEAR_LAYERS;
    let type: MistraMode = 'linear';
    let axisId: string | undefined;
    if (this.mode === 'areal') {
      this.requestedPoint += 1;
      layers = AREAL_LAYERS;
      type = 'areal';
      if (this.requestedPoint === 2) {
        axisId = this.result?.axisId;
      }
    }

    this.loading = true;
    this.refreshRender();
    try {
      const extConf = this.context.configManager.Config.extendedConfig;
      const mdConf = extConf?.['mistradistance'] as {serviceUrl?: string} | undefined;
      const serviceUrl = mdConf?.serviceUrl ?? '/mistradistance';
      const response = await fetch(serviceUrl, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          data: {
            point: [x, y],
            layers,
            type,
            requested_point: this.requestedPoint,
            asg_iliid: axisId,
            first_point: this.firstPoint,
          },
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = (await response.json()) as MistraApiResponse;
      this.handleResponse(data);
    } catch (err) {
      console.error('Mistradistance service error:', err);
      this.resetResults();
      this.state.infobox.elements.push({
        id: `mistra-${Math.random().toString(36).slice(2)}`,
        text: 'Erreur lors du calcul de référencement linéaire. Merci de contacter le SITN (sitn@ne.ch).',
        type: 'error',
        duration: 5000,
      });
    } finally {
      this.loading = false;
      this.refreshRender();
    }
  }

  private handleResponse(data: MistraApiResponse) {
    if (data.distance_error) {
      this.resetResults();
      this.distanceError = true;
      return;
    }
    if (data.wrong_axis_correspondance) {
      this.resetResults();
      this.wrongAxisError = true;
      return;
    }
    if (data.multigeometry) {
      this.resetResults();
      this.multigeometryError = true;
      return;
    }

    const features = new GeoJSON().readFeatures(data as object);

    if (this.mode !== 'areal' || this.requestedPoint === 1) {
      for (const feature of features) {
        const props = feature.getProperties();
        if (props['obj_type'] === 'axis') {
          this.result = this.toAxisResult(props);
        }
        if (props['obj_type'] !== 'end_point') {
          this.vectorSource.addFeature(feature);
        } else {
          this.firstPoint = (feature.getGeometry() as Point).getCoordinates();
        }
      }
    } else if (this.mode === 'areal' && this.requestedPoint === 2) {
      // Second click of an areal measurement: only the road polygon is displayed.
      this.vectorSource.clear();
      let intersectionLength = 0;
      let intersectionArea = 0;
      for (const feature of features) {
        const props = feature.getProperties();
        if (props['obj_type'] === 'axis') {
          this.result2 = this.toAxisResult(props);
          if (this.result) {
            intersectionLength += Math.abs(this.result.totalLength - this.result2.totalLength);
          }
        }
        if (props['obj_type'] === 'road_polygon') {
          this.vectorSource.addFeature(feature);
          intersectionArea += (feature.getGeometry() as Polygon).getArea();
        }
      }
      this.intersectionLength = intersectionLength;
      this.intersectionArea = intersectionArea;
    }
  }

  private toAxisResult(props: Record<string, unknown>): AxisResult {
    const layer = String(props['layer']);
    return {
      layer,
      axisType: AXIS_TYPE_LABELS[layer] ?? layer,
      axisName: String(props['axis_name']),
      sectorName: String(props['sector_name']),
      sectorLength: Number(props['sector_length']),
      totalLength: Number(props['total_length']),
      axisId: String(props['axis_id']),
    };
  }
}

export default SitnMistradistanceComponent;
